import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  ClinicalSessionProgress,
  ClinicalSessionStatus,
  ClinicalSessionStep,
  SaveSessionRequest,
} from '@/dtos/FlowClinicalSessions/index';
import { BasePerson } from '@/dtos/Persons/index';
import { useDeleteClinicalSession } from '@/hooks/ClinicalSession';
import { useGetFlowClinicalSession, useProcessStep2, useSaveSession } from '@/hooks/FlowClinical';
import { useGetPersons } from '@/hooks/Person';
import { usePermissions } from '@/hooks/usePermissions';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Edit,
  FileText,
  Loader2,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Step1, { Step1Handle } from './Step1/Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';

import StepIndicator from './StepIndicator';

import { STEPS, SessionState } from './types';

export default function FlowClinical() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionIdFromUrl = searchParams.get('sessionId');
  const getStepIndexFromParam = (stepParam: string | null): number | null => {
    if (!stepParam) return null;
    const parsed = Number(stepParam);
    if (!Number.isInteger(parsed)) return null;
    const stepIndex = parsed - 1; // URL is 1-based, state is 0-based
    if (stepIndex < 0 || stepIndex >= STEPS.length) return null;
    return stepIndex;
  };
  const stepIndexFromUrl = getStepIndexFromParam(searchParams.get('step'));
  const hasStepInUrl = stepIndexFromUrl !== null;
  const lastStepParamWrittenRef = useRef<string | null>(null);
  const step1Ref = useRef<Step1Handle>(null);
  const { isAdminOrMedic } = usePermissions();

  const processStep2Mutation = useProcessStep2();
  const saveSessionMutation = useSaveSession();
  const deleteSessionMutation = useDeleteClinicalSession();

  // Session state
  const [sessionState, setSessionState] = useState<SessionState>({
    sessionId: undefined,
    currentStepIndex: stepIndexFromUrl ?? 0,
    hasUnsavedChanges: false,
    selectedPerson: null,
    personFromResponse: null,
  });

  // Load existing session if sessionId is in URL or if we have a sessionId from state
  const currentSessionId = sessionIdFromUrl || sessionState.sessionId || '';
  const {
    data: existingSessionResponse,
    isLoading: isLoadingSession,
    refetch: refetchSession,
  } = useGetFlowClinicalSession(currentSessionId, { enabled: !!currentSessionId });

  const isEditMode = !!sessionIdFromUrl;
  const existingSession = existingSessionResponse?.isSuccess ? existingSessionResponse.data : null;

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [isPersonManuallyCleared, setIsPersonManuallyCleared] = useState(false);
  const [step3ErrorMessage, setStep3ErrorMessage] = useState<string | null>(null);
  const [step1CanSubmit, setStep1CanSubmit] = useState(false);
  const lastProcessedSessionIdRef = useRef<string | null>(null);
  const lastProcessedPersonIdRef = useRef<string | null>(null);
  const userNavigatedRef = useRef(false); // Track if user manually navigated
  const lastSyncedSessionIdRef = useRef<string | null>(null); // Track which session we've synced step index for

  // Step 1 specific state
  const [haveGdpr, setHaveGdpr] = useState(false);

  // Keep step index in sync with URL (?step=1..4), including browser back/forward/manual URL edits.
  // Guard against feedback loops when URL is updated by this component.
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (!stepParam) return;
    if (lastStepParamWrittenRef.current === stepParam) {
      lastStepParamWrittenRef.current = null;
      return;
    }
    if (stepIndexFromUrl === null) return;
    setSessionState((prev) => {
      if (prev.currentStepIndex === stepIndexFromUrl) return prev;
      userNavigatedRef.current = true;
      return { ...prev, currentStepIndex: stepIndexFromUrl };
    });
  }, [searchParams, stepIndexFromUrl]);

  // Persist current step to URL.
  useEffect(() => {
    const stepParam = String(sessionState.currentStepIndex + 1);
    const currentStepParam = searchParams.get('step');
    if (currentStepParam === stepParam) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('step', stepParam);
    lastStepParamWrittenRef.current = stepParam;
    setSearchParams(nextParams, { replace: true });
  }, [sessionState.currentStepIndex, searchParams, setSearchParams]);

  // Get all persons to find by personId
  const { data: allPersonsResponse } = useGetPersons();
  const allPersons = allPersonsResponse?.isSuccess ? allPersonsResponse.data || [] : [];

  // Find person by ID from session
  const fetchedPerson =
    existingSession?.personId && !sessionState.selectedPerson && !isPersonManuallyCleared
      ? allPersons.find((p) => p.id === existingSession.personId) || null
      : null;

  // Ref to track unsaved changes for beforeunload event
  const hasUnsavedChangesRef = useRef(sessionState.hasUnsavedChanges);

  // Keep ref in sync with state
  useEffect(() => {
    hasUnsavedChangesRef.current = sessionState.hasUnsavedChanges;
  }, [sessionState.hasUnsavedChanges]);

  // Handle browser/tab close warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Load existing session data when available
  useEffect(() => {
    if (!existingSession) return;

    const sessionId = existingSession.id;
    const personId = existingSession.person?.id;

    // Only process if this is a new session or person
    const isNewSession = sessionId !== lastProcessedSessionIdRef.current;
    const isNewPerson = personId !== lastProcessedPersonIdRef.current;

    if (isNewSession && isPersonManuallyCleared) {
      setIsPersonManuallyCleared(false);
    }

    // Set GDPR (only if value changed)
    if (existingSession.haveGdpr !== undefined && existingSession.haveGdpr !== haveGdpr) {
      setHaveGdpr(existingSession.haveGdpr);
    }

    // Calculate step index from progress (only update on initial load or if progress is ahead)
    if (existingSession.progress) {
      let stepIndex = 0;
      const currentStep = existingSession.progress?.currentStep;
      const foundIndex = STEPS.findIndex((s) => s.key === currentStep);
      console.log('[FlowClinical] Progress update:', {
        currentStep,
        foundIndex,
        currentStepIndex: sessionState.currentStepIndex,
        sessionLoaded,
        STEPS: STEPS.map(s => ({ key: s.key, label: s.label })),
      });
      
      if (foundIndex >= 0) {
        stepIndex = foundIndex;
      } else if (currentStep === ClinicalSessionStep.Completed) {
        // If Completed (5), show the last step (Guidance/Summary - index 3)
        stepIndex = STEPS.length - 1;
      }
      
      // Mark previous steps as completed
      const completed = new Set<number>();
      for (let i = 0; i < stepIndex; i++) {
        completed.add(i);
      }
      setCompletedSteps(completed);
      
      // Only update step index on initial load of this session or if progress is ahead
      // This ensures on page reload, we sync with the saved progress
      setSessionState((prev) => {
        // Check if this is the first time we're loading this session
        const currentSessionId = sessionId || existingSession?.id;
        const isNewSession = currentSessionId !== lastSyncedSessionIdRef.current;
        const isInitialLoad = !sessionLoaded || isNewSession;
        
      // On initial load of a session, always sync with progress
      // After that, update if progress is ahead. If backend moved to Summary,
      // always advance even if user navigated (to avoid staying on Results at 100%).
      const isProgressAhead = stepIndex > prev.currentStepIndex;
      const isProgressBehind = stepIndex < prev.currentStepIndex;
      const shouldUpdate = (!hasStepInUrl && isInitialLoad) ||
        (!userNavigatedRef.current && isProgressAhead) ||
        (isProgressAhead && currentStep === ClinicalSessionStep.Guidance) ||
        isProgressBehind;
        if (shouldUpdate && prev.currentStepIndex !== stepIndex) {
          console.log('[FlowClinical] Updating step index:', prev.currentStepIndex, '->', stepIndex, '(initial load:', isInitialLoad, 'new session:', isNewSession, 'sessionId:', currentSessionId, 'progress ahead:', stepIndex > prev.currentStepIndex, ')');
          if (isInitialLoad) {
            setSessionLoaded(true);
            lastSyncedSessionIdRef.current = currentSessionId || null;
          }
          return { ...prev, currentStepIndex: stepIndex };
        }
        return prev;
      });
    } else if (!sessionLoaded) {
      // If no progress yet, mark as loaded to prevent infinite loops
      setSessionLoaded(true);
    }

    // Update selectedPerson with latest data from session (only if person changed)
    if (existingSession.person && (isNewPerson || isNewSession) && !isPersonManuallyCleared) {
      const person = existingSession.person;
      const updatedPerson: BasePerson = {
        id: person.id,
        personInternalId: person.personInternalId,
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
        phone: person.phone,
        cnp: person.cnp,
        age: person.age,
        gender: person.gender,
        birthDate: person.birthDate,
        country: person.country,
        city: person.city,
        address: person.address,
        healthProfile: person.healthProfile
          ? {
              id: person.healthProfile.id,
              personId: person.healthProfile.personId,
              isSmoker: person.healthProfile.isSmoker,
              isFormerSmoker: person.healthProfile.isFormerSmoker,
              hasDiabetes: person.healthProfile.hasDiabetes,
              hasHypertension: person.healthProfile.hasHypertension,
              hasDyslipidemia: person.healthProfile.hasDyslipidemia,
              bodyMassIndex: person.healthProfile.bodyMassIndex,
              systolicBloodPressure: person.healthProfile.systolicBloodPressure,
              familyHistoryCardiovascularDisease: person.healthProfile.familyHistoryCardiovascularDisease,
            }
          : undefined,
      };

      // Update selectedPerson
      setSessionState((prev) => ({
        ...prev,
        selectedPerson: updatedPerson,
        personFromResponse: existingSession.person || null,
      }));
      lastProcessedPersonIdRef.current = personId || null;
    }

    // Update sessionId if it changed
    if (isNewSession) {
      setSessionState((prev) => ({ ...prev, sessionId: sessionId || undefined }));
      lastProcessedSessionIdRef.current = sessionId || null;
    }
  }, [
    existingSession?.id,
    existingSession?.haveGdpr,
    existingSession?.progress?.currentStep,
    existingSession?.person?.id,
    sessionLoaded,
    haveGdpr,
    isPersonManuallyCleared,
  ]);

  // Auto-select person when fetched by personId from session
  useEffect(() => {
    if (fetchedPerson && !sessionState.selectedPerson && !isPersonManuallyCleared) {
      setSessionState((prev) => ({
        ...prev,
        selectedPerson: fetchedPerson,
      }));
    }
  }, [fetchedPerson, sessionState.selectedPerson, isPersonManuallyCleared]);

  // Get current progress - always recalculate percentage based on current step
  const getCurrentProgress = useCallback((): ClinicalSessionProgress => {
    const stepKey = STEPS[sessionState.currentStepIndex]?.key ?? ClinicalSessionStep.Person;
    const effectiveStep = existingSession?.progress?.currentStep ?? stepKey;
    const percentageByStep: Record<ClinicalSessionStep, number> = {
      [ClinicalSessionStep.Person]: 0,
      [ClinicalSessionStep.Assessment]: 20,
      [ClinicalSessionStep.Results]: 60,
      [ClinicalSessionStep.Guidance]: 100,
      [ClinicalSessionStep.Completed]: 100,
    };

    const status = existingSession?.progress?.status === ClinicalSessionStatus.Completed || effectiveStep === ClinicalSessionStep.Completed
      ? ClinicalSessionStatus.Completed
      : (sessionState.currentStepIndex === 0 ? ClinicalSessionStatus.Draft : ClinicalSessionStatus.InProgress);
    const percentage = status === ClinicalSessionStatus.Completed ? 100 : (percentageByStep[effectiveStep] ?? 0);

    return {
      currentStep: effectiveStep,
      totalSteps: STEPS.length,
      status,
      percentage,
    };
  }, [sessionState.currentStepIndex, existingSession?.progress?.currentStep]);

  // Save as draft
  const handleSaveAsDraft = async () => {
    const request: SaveSessionRequest = {
      sessionId: sessionState.sessionId || null,
      progress: getCurrentProgress(),
    };

    const result = await saveSessionMutation.mutateAsync(request);
    if (result.isSuccess && result.data) {
      setSessionState((prev) => ({
        ...prev,
        sessionId: result.data!.sessionId,
        hasUnsavedChanges: false,
      }));
    }
    return result.isSuccess;
  };

  // Handle exit dialog actions
  const handleExitWithSave = async () => {
    const success = await handleSaveAsDraft();
    if (success) {
      setShowExitDialog(false);
      if (pendingNavigation) {
        navigate(pendingNavigation);
      }
    }
  };

  const handleExitWithoutSave = () => {
    setSessionState((prev) => ({ ...prev, hasUnsavedChanges: false }));
    setShowExitDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const handleCancelExit = () => {
    setShowExitDialog(false);
    setPendingNavigation(null);
  };

  // Helper to navigate with unsaved changes check
  const safeNavigate = (path: string) => {
    // In edit mode, just navigate directly (no save draft option)
    if (isEditMode) {
      navigate(path);
    } else if (sessionState.hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowExitDialog(true);
    } else {
      navigate(path);
    }
  };

  // Refetch session data when navigating to a step that needs it
  useEffect(() => {
    if (currentSessionId && (sessionState.currentStepIndex === 0 || sessionState.currentStepIndex === 1)) {
      // Refetch session data when navigating to Step1 or Step2 to ensure we have latest data
      refetchSession();
    }
  }, [sessionState.currentStepIndex, currentSessionId, refetchSession]);

  // Handle step navigation
  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < STEPS.length) {
      userNavigatedRef.current = true; // Mark that user manually navigated
      if (step3ErrorMessage && stepIndex !== 1) {
        setStep3ErrorMessage(null);
      }
      setSessionState((prev) => ({ ...prev, currentStepIndex: stepIndex }));
    }
  };

  const goToNextStep = () => {
    if (sessionState.currentStepIndex < STEPS.length - 1) {
      setCompletedSteps((prev) => new Set(Array.from(prev).concat(sessionState.currentStepIndex)));
      goToStep(sessionState.currentStepIndex + 1);
    }
  };

  const goToPreviousStep = async () => {
    if (sessionState.currentStepIndex > 0) {
      // Refetch session data when going back to ensure we have latest data
      if (currentSessionId) {
        await refetchSession();
      }
      goToStep(sessionState.currentStepIndex - 1);
    }
  };

  // Handle person selection
  const handleSelectPerson = (person: BasePerson) => {
    if (step3ErrorMessage) {
      setStep3ErrorMessage(null);
    }
    setIsPersonManuallyCleared(false);
    setSessionState((prev) => ({
      ...prev,
      selectedPerson: person,
      hasUnsavedChanges: true,
    }));
  };

  // Handle deselect person - only allowed in create mode
  const handleDeselectPerson = () => {
    setIsPersonManuallyCleared(true);
    setSessionState((prev) => ({
      ...prev,
      selectedPerson: null,
      personFromResponse: null,
      hasUnsavedChanges: true,
    }));
  };

  // Handle Step 1 submission
  const handleStep1Submit = async () => {
    if (step1Ref.current) {
      await step1Ref.current.submit();
    }
  };

  // Check if can proceed to next step
  // Compute directly from state - this ensures immediate updates when state changes
  // For Step1: need GDPR checked AND (person selected OR in create mode)
  const canProceedStep1 = useMemo(() => {
    return step1CanSubmit || (step1Ref.current?.canSubmit() ?? false);
  }, [step1CanSubmit]);

  // Render current step content
  const renderStepContent = () => {
    switch (sessionState.currentStepIndex) {
      case 0:
        return (
          <Step1
            ref={step1Ref}
            sessionId={sessionState.sessionId}
            isEditMode={isEditMode}
            selectedPerson={sessionState.selectedPerson}
            haveGdpr={haveGdpr}
            personIdFromSession={isPersonManuallyCleared ? null : existingSession?.personId || null}
            onGdprChange={(checked) => {
              setHaveGdpr(checked);
              setSessionState((prev) => ({ ...prev, hasUnsavedChanges: true }));
              // Force re-evaluation of canProceedStep1
            }}
            onPersonSelect={handleSelectPerson}
            onPersonDeselect={handleDeselectPerson}
            onCanSubmitChange={setStep1CanSubmit}
            onSuccess={async (newSessionId) => {
              if (step3ErrorMessage) {
                setStep3ErrorMessage(null);
              }
              setSessionState((prev) => ({
                ...prev,
                sessionId: newSessionId,
                hasUnsavedChanges: false,
              }));
              // Refetch session data after Step1 completes to get updated personId and other data
              if (newSessionId) {
                await refetchSession();
              }
              goToNextStep();
            }}
          />
        );

      case 1: // Assessment (Step 2 in the flow, but index 1)
        if (!sessionState.sessionId) {
          return (
            <Card>
              <CardContent className='py-8 text-center text-muted-foreground'>
                <p>Vă rugăm să completați mai întâi pasul 1 (Date Personale).</p>
              </CardContent>
            </Card>
          );
        }
        // Check if Step 1 is completed (session must have a personId)
        // If we just completed Step 1, the session might not be refetched yet
        // So we check: existingSession?.personId OR if we have a selectedPerson (meaning Step 1 was just completed)
        // OR if we're loading the session (give it time to load)
        const hasPersonId =
          (!isPersonManuallyCleared && existingSession?.personId) || sessionState.selectedPerson;
        const isSessionLoading = isLoadingSession && !!currentSessionId;

        if (!hasPersonId && !isSessionLoading) {
          return (
            <Card>
              <CardContent className='py-8 text-center text-muted-foreground'>
                <p>Pasul 1 (Identificare Pacient) trebuie completat înainte de a continua la Pasul 2.</p>
                <Button variant='outline' className='mt-4' onClick={() => goToStep(0)}>
                  Mergi la Pasul 1
                </Button>
              </CardContent>
            </Card>
          );
        }

        // Show loading while session is being fetched
        if (isSessionLoading && !hasPersonId) {
          return (
            <Card>
              <CardContent className='py-8 text-center text-muted-foreground'>
                <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4' />
                <p>Se încarcă datele sesiunii...</p>
              </CardContent>
            </Card>
          );
        }
        return (
          <Step2
            sessionId={sessionState.sessionId}
            assessmentExchanges={existingSession?.assessmentExchanges}
            person={
              (!isPersonManuallyCleared ? existingSession?.person : null) ||
              (sessionState.selectedPerson as unknown as BasePerson)
            }
            onSuccess={async (newSessionId) => {
              if (step3ErrorMessage) {
                setStep3ErrorMessage(null);
              }
              setSessionState((prev) => ({
                ...prev,
                sessionId: newSessionId,
                hasUnsavedChanges: false,
              }));
              // Refetch session data after Step2 completes to get updated assessment data
              if (newSessionId) {
                await refetchSession();
              }
              setCompletedSteps((prev) => {
                const newSet = new Set(Array.from(prev));
                newSet.add(1);
                return newSet;
              });
              goToNextStep();
            }}
            onBack={async () => {
              // Refetch session data when going back to ensure we have latest person data
              if (currentSessionId) {
                await refetchSession();
              }
              goToPreviousStep();
            }}
          />
        );

      case 2: // Results (Step3)
        if (!sessionState.sessionId) {
          return (
            <Card>
              <CardContent className='py-8 text-center text-muted-foreground'>
                <FileText className='h-12 w-12 mx-auto mb-4 opacity-50' />
                <p>Nu există o sesiune activă.</p>
              </CardContent>
            </Card>
          );
        }
        return (
          <Step3
            sessionId={sessionState.sessionId}
            riskCalculations={existingSession?.riskCalculations}
            currentStep={existingSession?.progress?.currentStep}
            onSuccess={async (newSessionId) => {
              if (step3ErrorMessage) {
                setStep3ErrorMessage(null);
              }
              setSessionState((prev) => ({
                ...prev,
                sessionId: newSessionId,
                hasUnsavedChanges: false,
              }));
              // Refetch session data after Step3 completes to get updated progress
              if (newSessionId) {
                await refetchSession();
              }
              setCompletedSteps((prev) => {
                const newSet = new Set(Array.from(prev));
                newSet.add(2);
                return newSet;
              });
              goToNextStep();
            }}
            onBack={async () => {
              // Refetch session data when going back
              if (currentSessionId) {
                await refetchSession();
              }
              goToPreviousStep();
            }}
            onError={(errorMessage) => {
              const normalizedMessage = errorMessage?.trim() || 'Failed to process step 3';
              setStep3ErrorMessage(normalizedMessage);
              userNavigatedRef.current = true;
              setCompletedSteps((prev) => {
                const next = new Set(Array.from(prev));
                next.delete(2);
                return next;
              });
              goToStep(1);
            }}
          />
        );

      case 3: // Summary (Step4)
        if (!sessionState.sessionId) {
          return (
            <Card>
              <CardContent className='py-8 text-center text-muted-foreground'>
                <CheckCircle2 className='h-12 w-12 mx-auto mb-4 opacity-50' />
                <p>Nu există o sesiune activă.</p>
              </CardContent>
            </Card>
          );
        }
        return (
          <Step4
            sessionId={sessionState.sessionId}
            riskCalculations={existingSession?.riskCalculations}
            assessmentExchanges={existingSession?.assessmentExchanges}
            isCompleted={existingSession?.progress?.status === ClinicalSessionStatus.Completed}
            onSuccess={async (newSessionId) => {
              setSessionState((prev) => ({
                ...prev,
                sessionId: newSessionId,
                hasUnsavedChanges: false,
              }));
              // Refetch session data after Step4 completes
              if (newSessionId) {
                await refetchSession();
              }
              setCompletedSteps((prev) => {
                const newSet = new Set(Array.from(prev));
                newSet.add(3);
                return newSet;
              });
            }}
            onBack={async () => {
              // Refetch session data when going back
              if (currentSessionId) {
                await refetchSession();
              }
              goToPreviousStep();
            }}
          />
        );

      default:
        return null;
    }
  };

  // Get step action handler
  const getStepAction = () => {
    switch (sessionState.currentStepIndex) {
      case 0:
        return handleStep1Submit;
      default:
        return goToNextStep;
    }
  };

  const isProcessing = processStep2Mutation.isPending || saveSessionMutation.isPending;

  // Show loading screen while loading existing session
  if (isEditMode && isLoadingSession) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[400px]'>
        <Loader2 className='h-12 w-12 animate-spin text-primary' />
        <p className='mt-4 text-lg text-muted-foreground'>Se încarcă sesiunea...</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight flex items-center gap-2'>
            {isEditMode ? (
              <>
                <Edit className='h-8 w-8' />
                Modifică Sesiune Clinică
              </>
            ) : (
              'Sesiune Clinică Nouă'
            )}
          </h1>
          <div className='text-muted-foreground mt-2'>
            {sessionState.sessionId ? (
              <span className='flex items-center gap-2'>
                ID Sesiune:{' '}
                <Badge variant='outline' className='font-mono'>
                  {sessionState.sessionId.slice(0, 8)}...
                </Badge>
                {existingSession?.personInternalId && (
                  <>
                    <span className='mx-1'>•</span>
                    Pacient:{' '}
                    <Badge variant='secondary' className='font-mono'>
                      {existingSession.personInternalId}
                    </Badge>
                  </>
                )}
              </span>
            ) : (
              'Creați o sesiune clinică nouă'
            )}
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {isEditMode ? (
            // Edit mode: only Delete button
            <Button variant='destructive' onClick={() => setShowDeleteDialog(true)} disabled={isProcessing}>
              <Trash2 className='h-4 w-4 mr-2' />
              Șterge Sesiunea
            </Button>
          ) : (
            // Create mode: Save Draft (only for Admin/Medic) and Close buttons
            <>
              {isAdminOrMedic() && (
                <Button
                  variant='outline'
                  onClick={handleSaveAsDraft}
                  disabled={isProcessing || !sessionState.hasUnsavedChanges}
                >
                  {saveSessionMutation.isPending ? (
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  ) : (
                    <Save className='h-4 w-4 mr-2' />
                  )}
                  Salvează Draft
                </Button>
              )}
              <Button variant='ghost' onClick={() => safeNavigate('/clinical-sessions')}>
                <X className='h-4 w-4 mr-2' />
                Închide
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className='space-y-2'>
        <div className='flex justify-between text-sm text-muted-foreground'>
          <span>Progres</span>
          <span>
            {existingSession?.progress?.percentage ?? 
             (!sessionState.sessionId && !sessionIdFromUrl ? 0 : getCurrentProgress().percentage)}%
          </span>
        </div>
        <Progress 
          value={existingSession?.progress?.percentage ?? 
                 (!sessionState.sessionId && !sessionIdFromUrl ? 0 : getCurrentProgress().percentage)} 
          className='h-2' 
        />
      </div>

      {/* Step Indicator */}
      <StepIndicator currentIndex={sessionState.currentStepIndex} completedSteps={completedSteps} />

      {/* Step Content */}
      {sessionState.currentStepIndex === 1 && step3ErrorMessage && (
        <Card className='border-destructive/30 bg-destructive/5'>
          <CardContent className='py-4 flex items-start gap-3'>
            <AlertTriangle className='h-5 w-5 text-destructive mt-0.5' />
            <div>
              <p className='font-medium text-destructive'>Eroare la calculul rezultatelor (Pasul 3)</p>
              <p className='text-sm text-destructive mt-1'>{step3ErrorMessage}</p>
            </div>
          </CardContent>
        </Card>
      )}
      <div className='min-h-[400px]'>{renderStepContent()}</div>

      {/* Navigation Buttons - Only show for Step1, other steps handle their own navigation */}
      {sessionState.currentStepIndex === 0 && (
        <div className='flex items-center justify-between pt-4 border-t'>
          <Button
            variant='outline'
            onClick={goToPreviousStep}
            disabled={sessionState.currentStepIndex === 0 || isProcessing}
          >
            <ArrowLeft className='h-4 w-4 mr-2' />
            Înapoi
          </Button>

          <div className='flex items-center gap-2'>
            {sessionState.currentStepIndex < STEPS.length - 1 ? (
              <Button
                onClick={getStepAction()}
                disabled={isProcessing || (sessionState.currentStepIndex === 0 && !canProceedStep1)}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Se procesează...
                  </>
                ) : (
                  <>
                    Continuă
                    <ArrowRight className='h-4 w-4 ml-2' />
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={() => {}} disabled={isProcessing}>
                <Check className='h-4 w-4 mr-2' />
                Finalizează
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Exit Confirmation Dialog - only for create mode */}
      {!isEditMode && (
        <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className='flex items-center gap-2'>
                <AlertTriangle className='h-5 w-5 text-amber-500' />
                Modificări nesalvate
              </AlertDialogTitle>
              <AlertDialogDescription>
                Aveți modificări nesalvate în sesiunea curentă. Doriți să salvați sesiunea ca draft înainte de a părăsi
                pagina?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className='gap-2 sm:gap-0'>
              <AlertDialogCancel onClick={handleCancelExit}>Anulează</AlertDialogCancel>
              <Button variant='outline' onClick={handleExitWithoutSave}>
                Părăsește fără salvare
              </Button>
              <Button onClick={handleExitWithSave} disabled={saveSessionMutation.isPending}>
                {saveSessionMutation.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className='h-4 w-4 mr-2' />
                    Salvează și părăsește
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Delete Confirmation Dialog - only for edit mode */}
      {isEditMode && (
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className='flex items-center gap-2'>
                <Trash2 className='h-5 w-5 text-destructive' />
                Confirmați ștergerea
              </AlertDialogTitle>
              <AlertDialogDescription>
                Sunteți sigur că doriți să ștergeți această sesiune clinică? Această acțiune este ireversibilă.
                {existingSession?.personInternalId && (
                  <span className='block mt-2'>
                    Pacient: <strong>{existingSession.personInternalId}</strong>
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anulează</AlertDialogCancel>
              <Button
                variant='destructive'
                onClick={async () => {
                  if (sessionIdFromUrl) {
                    const result = await deleteSessionMutation.mutateAsync(sessionIdFromUrl);
                    if (result.isSuccess) {
                      navigate('/clinical-sessions');
                    }
                  }
                }}
                disabled={deleteSessionMutation.isPending}
              >
                {deleteSessionMutation.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Se șterge...
                  </>
                ) : (
                  <>
                    <Trash2 className='h-4 w-4 mr-2' />
                    Șterge
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
